// ═══════════════════════════════════════════════════════════════
// notificationUtils.js - 브라우저 Window Notification 유틸
// ═══════════════════════════════════════════════════════════════

const NOTIFICATION_ICON = '/favicon.ico'; // 앱 아이콘 경로

/**
 * 브라우저 알림 권한 요청
 * - 앱 최초 로드 시 한 번 호출해두면 됩니다.
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('⚠️ 이 브라우저는 Notification API를 지원하지 않습니다.');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    console.log('🔔 알림 권한 결과:', permission);
    return permission;
  }

  return Notification.permission;
}

/**
 * 현재 알림 권한 상태 반환
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * 브라우저 윈도우 알림 표시
 * - 탭이 포커스 중이면 표시하지 않음 (이미 보고 있으므로)
 * - 권한이 없으면 자동으로 요청 후 표시
 *
 * @param {object} options
 * @param {string} options.title       - 알림 제목
 * @param {string} options.body        - 알림 본문
 * @param {string} [options.icon]      - 알림 아이콘 URL
 * @param {string} [options.tag]       - 동일 tag는 덮어씌워짐 (중복 방지)
 * @param {boolean} [options.forceShow] - true면 포커스 중에도 표시
 * @param {function} [options.onClick] - 알림 클릭 시 콜백
 * @returns {Promise<Notification|null>}
 */
export async function showWindowNotification({
  title,
  body,
  icon = NOTIFICATION_ICON,
  tag,
  forceShow = false,
  onClick,
}) {
  if (!('Notification' in window)) return null;

  // 탭이 포커스 중이면 윈도우 알림 생략 (스낵바로 충분)
  if (!forceShow && document.hasFocus()) return null;

  // 권한 확인 / 요청
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await requestNotificationPermission();
  }
  if (permission !== 'granted') return null;

  const notification = new Notification(title, {
    body,
    icon,
    tag: tag || `mention-${Date.now()}`,
    requireInteraction: false, // 자동으로 사라짐
  });

  // 클릭하면 해당 탭으로 포커스 + 콜백
  notification.onclick = (e) => {
    e.preventDefault();
    window.focus();
    if (onClick) onClick();
    notification.close();
  };

  // 10초 후 자동 닫기 (requireInteraction: false 지원 안 하는 브라우저 대비)
  setTimeout(() => notification.close(), 10000);

  return notification;
}

/**
 * 멘션 전용 윈도우 알림
 * @param {object} mentionNotification - WebSocket에서 받은 멘션 알림 payload
 * @param {function} [onClickGoToChat]  - 알림 클릭 시 채팅창으로 이동 콜백
 */
export async function showMentionWindowNotification(mentionNotification, onClickGoToChat) {
  const { fromUserNm, messagePreview, chatRoomUuid } = mentionNotification;

  return showWindowNotification({
    title: `💬 ${fromUserNm || '누군가'}님이 회원님을 멘션했습니다`,
    body: messagePreview
      ? `"${messagePreview}"`
      : '채팅을 확인해보세요.',
    tag: `mention-${chatRoomUuid || Date.now()}`, // 같은 채널 알림은 덮어씌움
    onClick: onClickGoToChat,
  });
}
